import { createBrowserHistory } from 'history'
import { lensProp, over, pipe } from 'ramda'
import { createStore } from 'redux-dynamic-modules'
import { IEventModule } from 'redux-dynamic-modules-beacon'
import { getSagaExtension, ISagaModule } from 'redux-dynamic-modules-saga'
import { createReduxHistoryContext } from 'redux-first-history'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import createSagaMiddleware from 'redux-saga'

import { apolloClient } from '@app/common/apollo/apolloClient'

import AuthAction from '../../modules/Auth/actions'
import { subscribeToAuthSessionMessages } from '../../modules/Auth/authSessionBroadcast'
import { migrateLegacyPersistedSession } from '../../modules/Auth/authSessionRecord'
import { AuthState } from '../../modules/Auth/models'
import auth from '../../modules/Auth/reducers'
import { adoptEndedAuthSession, syncAuthSession } from '../../modules/Auth/syncAuthSession'

import { legacySagas } from './legacySagas'

// Prepare routeMiddleware for store
const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  reduxTravelling: true
})

// Prepare sagaMiddleware for store
export const sagaMiddleware = createSagaMiddleware()

// Prepare getLegacyModule for store
const createPersistConfig = (stateSlice: string, blacklist?: string[]) => ({
  key: stateSlice,
  storage,
  blacklist
})

const legacyModuleReducers = {
  auth,
  router: routerReducer
}

function persistReducerForKey(key: string, blacklist?: string[]) {
  return over(lensProp(key), (reducer) =>
    persistReducer(createPersistConfig(key, blacklist), reducer)
  )
}

export const AUTH_PERSIST_KEY = 'auth'

const AUTH_FIELDS_OWNED_BY_THE_SESSION_RECORD: (keyof AuthState)[] = ['token', 'refreshToken']

const legacyModuleReducersWithRootReducer = pipe(
  persistReducerForKey(AUTH_PERSIST_KEY, AUTH_FIELDS_OWNED_BY_THE_SESSION_RECORD)
)(legacyModuleReducers)

export function getLegacyModule(): IEventModule<any> & ISagaModule<any> {
  return {
    id: 'legacy',
    reducerMap: legacyModuleReducersWithRootReducer as any,
    sagas: legacySagas
  }
}

export function getRouterExtension() {
  return {
    middleware: [routerMiddleware]
  }
}

// Create store with all assets prepared above. Export persisted store
export const store = createStore(
  {
    initialState: {},
    extensions: [getSagaExtension({ graphQLClient: apolloClient }), getRouterExtension()]
  },
  getLegacyModule()
)

migrateLegacyPersistedSession()
syncAuthSession(store)

subscribeToAuthSessionMessages({
  onRefreshedAuthSession: (session) => store.dispatch(AuthAction.onRefreshSuccess(session)),
  onEndedAuthSession: () => adoptEndedAuthSession(store)
})

// Keep the auth state in sync with other tabs when this tab regains focus
window.addEventListener('focus', () => syncAuthSession(store))

export const persistor = persistStore(store)
export const history = createReduxHistory(store)
